/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import "./ModalCarousel.css";
import ProjectModal from "../projectModal/ProjectModal";
import { ProjectModalContent, CompanyModalContent } from "../../types";
import CompanyModal from "../companyModal/CompanyModal";
import Portal from "../portal/Portal";
import { waveform } from "ldrs";

// Register the waveform loader
waveform.register();

interface ModalCarouselProps {
    content: ProjectModalContent[] | CompanyModalContent[];
    onClose?: () => void;
    initialIndex: number;
    onImageClick: (
        imageSrc: string,
        objectFit: "cover" | "contain" | "fill" | "scale-down" | "none",
        descriptiveTitle?: string
    ) => void;
    onIndexChange?: (newIndex: number) => void;
}

const calculateModalWidth = (): number => {
    const viewportWidth = window.innerWidth;

    if (viewportWidth <= 600) {
        // Mobile: 100% of viewport width minus padding
        return viewportWidth - 48;
    } else if (viewportWidth <= 1200) {
        // Tablet: responsive width but capped to fit viewport
        const calculatedWidth = 400 + (12 * viewportWidth) / 100 - 48;
        return Math.min(calculatedWidth, viewportWidth - 80); // Ensure some margin
    } else {
        // Desktop: maintain aspect ratio but ensure it fits viewport
        const calculatedWidth =
            (500 + 24 * (window.innerHeight / 100) - 48 - 24) * 1.6;
        return Math.min(calculatedWidth, viewportWidth - 120); // Ensure it fits with margins
    }
};

const calculateModalGap = (): number => {
    if (window.innerWidth <= 600) {
        return 10;
    } else {
        return 50;
    }
};

const ModalCarousel: React.FC<ModalCarouselProps> = ({
    content,
    onClose,
    initialIndex,
    onImageClick,
    onIndexChange,
}) => {
    const [isClosing, setIsClosing] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [modalWidth, setModalWidth] = useState(calculateModalWidth());
    const [modalGap, setModalGap] = useState(calculateModalGap());
    const [hoverZoneWidth, setHoverZoneWidth] = useState(
        (window.innerWidth - modalWidth) / 2
    );
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [touchStartY, setTouchStartY] = useState<number | null>(null);
    const [touchEndY, setTouchEndY] = useState<number | null>(null);

    // New state for tracking swipe animation
    const [swipeOffset, setSwipeOffset] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    // New state for temporary interaction blocks
    const [isInteractionBlocked, setIsInteractionBlocked] = useState(true);
    const resizeTimerRef = useRef<NodeJS.Timeout | null>(null);
    const trackRef = useRef<HTMLDivElement>(null);

    // Required minimum distance between touch start and touch end to be detected as swipe
    const minSwipeDistance = 30;
    // Maximum vertical movement allowed for a horizontal swipe to be valid
    const maxVerticalMovement = 30;
    // Duration in ms to block interactions after initial load or resize
    const blockDuration = 2000;

    // Check if we're on mobile
    const isMobile = () => window.innerWidth <= 600;

    // Detect iOS
    const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    // Add error boundary state
    const [hasError, setHasError] = useState(false);

    // Add a function to safely handle errors
    const handleError = (error: any, info: string) => {
        setHasError(true);

        // Log error to hidden div for debugging on iOS
        if (isIOS) {
            const errorDiv =
                document.getElementById("modal-error-log") ||
                (() => {
                    const div = document.createElement("div");
                    div.id = "modal-error-log";
                    div.style.display = "none";
                    document.body.appendChild(div);
                    return div;
                })();

            errorDiv.setAttribute("data-error", String(error));
            errorDiv.setAttribute("data-info", info);
        }
    };

    // Add iOS-specific optimizations
    useEffect(() => {
        if (isIOS) {
            // Reduce animation complexity on iOS
            document.documentElement.classList.add("ios-device");

            // Add error listener for unhandled errors
            const handleGlobalError = (event: ErrorEvent) => {
                event.preventDefault();
                handleError(event.error, event.message);
                return true;
            };

            // Add Safari-specific viewport change listener
            let lastHeight = window.innerHeight;
            let lastWidth = window.innerWidth;

            const checkForIOSToolbarChange = () => {
                const newHeight = window.innerHeight;
                const newWidth = window.innerWidth;

                // If height changed significantly (toolbar appeared/disappeared)
                // or if width changed (orientation change)
                if (
                    Math.abs(newHeight - lastHeight) > 50 ||
                    Math.abs(newWidth - lastWidth) > 50
                ) {
                    // Block interactions during viewport change
                    setIsInteractionBlocked(true);

                    // Clear any existing timer
                    if (resizeTimerRef.current) {
                        clearTimeout(resizeTimerRef.current);
                    }

                    // Reset offset and cancel any ongoing animations
                    setSwipeOffset(0);
                    setIsAnimating(false);

                    // Set timer to unblock
                    resizeTimerRef.current = setTimeout(() => {
                        setIsInteractionBlocked(false);
                        resizeTimerRef.current = null;
                    }, blockDuration);
                }

                lastHeight = newHeight;
                lastWidth = newWidth;
            };

            // Check for iOS toolbar changes using both scroll and resize events
            window.addEventListener("scroll", checkForIOSToolbarChange);
            window.addEventListener("resize", checkForIOSToolbarChange);
            window.addEventListener(
                "orientationchange",
                checkForIOSToolbarChange
            );

            window.addEventListener("error", handleGlobalError);

            return () => {
                document.documentElement.classList.remove("ios-device");
                window.removeEventListener("error", handleGlobalError);
                window.removeEventListener("scroll", checkForIOSToolbarChange);
                window.removeEventListener("resize", checkForIOSToolbarChange);
                window.removeEventListener(
                    "orientationchange",
                    checkForIOSToolbarChange
                );
                if (resizeTimerRef.current) {
                    clearTimeout(resizeTimerRef.current);
                }
            };
        }
    }, [isIOS]);

    // Initial interaction block on mount
    useEffect(() => {
        // Only block interactions on iOS devices
        if (isIOS) {
            setIsInteractionBlocked(true);
            const timer = setTimeout(() => {
                setIsInteractionBlocked(false);
            }, blockDuration);

            return () => clearTimeout(timer);
        }
    }, []);

    // Update both modal width and hover zone width on resize
    useEffect(() => {
        const handleResize = () => {
            // Only block interactions on iOS devices during resize
            if (isIOS) {
                setIsInteractionBlocked(true);

                // Clear any existing resize timer
                if (resizeTimerRef.current) {
                    clearTimeout(resizeTimerRef.current);
                }
            }

            const newModalWidth = calculateModalWidth();
            setModalWidth(calculateModalWidth);
            setModalGap(calculateModalGap());
            setHoverZoneWidth((window.innerWidth - newModalWidth) / 2);

            // Reset swipe offset to avoid positioning issues
            setSwipeOffset(0);

            // Set timer to unblock interactions after resize completes
            if (isIOS) {
                resizeTimerRef.current = setTimeout(() => {
                    setIsInteractionBlocked(false);
                    resizeTimerRef.current = null;
                }, blockDuration);
            }
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            if (resizeTimerRef.current) {
                clearTimeout(resizeTimerRef.current);
            }
        };
    }, [modalWidth, modalGap]);

    // Add keyboard event listener for both arrow keys and escape
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft" && currentIndex > 0) {
                updateCurrentIndex(currentIndex - 1);
            } else if (
                e.key === "ArrowRight" &&
                currentIndex < content.length - 1
            ) {
                updateCurrentIndex(currentIndex + 1);
            } else if (e.key === "Escape") {
                handleClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        // Cleanup
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [currentIndex, content.length]); // Dependencies

    // Add effect to manage body scroll
    useEffect(() => {
        // Add modal-open class to body when modal opens
        document.body.classList.add("modal-open");

        // Cleanup function when component unmounts
        return () => {
            // Remove modal-open class when modal closes
            document.body.classList.remove("modal-open");
        };
    }, []);

    // Helper function to update currentIndex and notify parent
    const updateCurrentIndex = (newIndex: number) => {
        setCurrentIndex(newIndex);
        if (onIndexChange) {
            onIndexChange(newIndex);
        }
    };

    const handleClose = () => {
        // Remove modal-open class when closing
        document.body.classList.remove("modal-open");
        setIsClosing(true);
        setTimeout(() => {
            onClose?.();
        }, 500);
    };

    const handleModalClick = (index: number) => {
        updateCurrentIndex(index);
    };

    const handleNavigate = (
        direction: "prev" | "next",
        e: React.MouseEvent
    ) => {
        e.stopPropagation(); // Prevent closing the modal
        if (direction === "prev" && currentIndex > 0) {
            updateCurrentIndex(currentIndex - 1);
        } else if (direction === "next" && currentIndex < content.length - 1) {
            updateCurrentIndex(currentIndex + 1);
        }
    };

    const getTrackStyle = () => {
        const baseOffset = currentIndex * (modalWidth + modalGap);
        const totalOffset = baseOffset + swipeOffset;
        const leftOffset = `calc(50% - ${modalWidth / 2}px)`;

        // Use different transition timing based on device and animation state
        let transitionTiming;

        if (window.innerWidth <= 600) {
            // For mobile, let CSS handle the transition when animating
            transitionTiming = isAnimating ? undefined : "none";
        } else {
            // Desktop transition - use original cubic bezier
            transitionTiming = isIOS
                ? "transform 0.5s ease-out, -webkit-transform 0.5s ease-out"
                : "opacity 0.5s cubic-bezier(0.165,0.84,0.44,1), transform 1.7s cubic-bezier(.06,.99,.34,.99), -webkit-transform 1.7s cubic-bezier(.06,.99,.34,.99), visibility 0s 0s";
        }

        return {
            transform: `translate3d(${-Math.round(totalOffset)}px, 0, 0)`,
            WebkitTransform: `translate3d(${-Math.round(totalOffset)}px, 0, 0)`,
            transition: transitionTiming,
            left: leftOffset,
            // Add additional iOS optimizations with proper TypeScript types
            WebkitBackfaceVisibility: "hidden" as const,
            backfaceVisibility: "hidden" as const,
            WebkitPerspective: 1000,
            perspective: 1000,
            willChange: "transform",
            maxWidth: "100vw", // Ensure track doesn't exceed viewport width
            position: "absolute" as const,
            // Add positioning constraints
            boxSizing: "border-box" as const,
        };
    };

    const getHoverZoneStyle = () => {
        return {
            width: `${hoverZoneWidth}px`,
        };
    };

    const onTouchStart = (e: React.TouchEvent) => {
        // Don't handle touch events during the blocked period on iOS
        if (isInteractionBlocked && isIOS) {
            return;
        }

        // Don't prevent default as it blocks click events
        setTouchEnd(null); // Reset touchEnd
        setTouchEndY(null); // Reset touchEndY
        setTouchStart(e.targetTouches[0].clientX);
        setTouchStartY(e.targetTouches[0].clientY);
        setIsAnimating(false);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        // Don't handle touch events during the blocked period on iOS
        if (isInteractionBlocked && isIOS) {
            return;
        }

        // Get current touch position
        const touchX = e.targetTouches[0].clientX;
        const touchY = e.targetTouches[0].clientY;

        // Calculate distance moved
        if (touchStart !== null && touchStartY !== null) {
            const horizontalDistance = touchStart - touchX;
            const verticalDistance = Math.abs(touchStartY - touchY);

            // Check if this is primarily a horizontal swipe
            const isHorizontalSwipe =
                Math.abs(horizontalDistance) > verticalDistance &&
                verticalDistance <= maxVerticalMovement;

            // Instead of trying to prevent default which causes the error,
            // we'll just handle the swipe logic

            // Only update swipe offset if it's a horizontal swipe
            if (isHorizontalSwipe) {
                // Calculate how much to move based on finger position
                // Negative value means swipe right, positive means swipe left
                const newOffset = touchStart - touchX;

                // Apply resistance at the edges
                if (
                    (currentIndex === 0 && newOffset < 0) ||
                    (currentIndex === content.length - 1 && newOffset > 0)
                ) {
                    // Apply 70% resistance at edges
                    setSwipeOffset(newOffset * 0.3);
                } else {
                    setSwipeOffset(newOffset);
                }
            }
        }

        setTouchEnd(touchX);
        setTouchEndY(touchY);
    };

    const onTouchEnd = (e: React.TouchEvent) => {
        // Don't handle touch events during the blocked period on iOS
        if (isInteractionBlocked && isIOS) {
            return;
        }

        if (!touchStart || !touchEnd || !touchStartY || !touchEndY) return;

        const horizontalDistance = touchStart - touchEnd;
        const verticalDistance = Math.abs(touchStartY - touchEndY);

        // Only process as a horizontal swipe if vertical movement is limited
        // and horizontal movement is greater than vertical movement
        const isHorizontalSwipe =
            Math.abs(horizontalDistance) > verticalDistance &&
            verticalDistance <= maxVerticalMovement;

        if (isHorizontalSwipe) {
            setIsAnimating(true);

            // Use minSwipeDistance instead of percentage-based threshold for smaller required movement
            // But still use percentage for larger screens to maintain natural feel
            const swipeThreshold = isMobile()
                ? minSwipeDistance
                : (modalWidth + modalGap) * 0.3;

            if (Math.abs(horizontalDistance) > swipeThreshold) {
                // Swipe was significant enough to change index
                if (
                    horizontalDistance > 0 &&
                    currentIndex < content.length - 1
                ) {
                    // Swipe left - go to next
                    updateCurrentIndex(currentIndex + 1);
                } else if (horizontalDistance < 0 && currentIndex > 0) {
                    // Swipe right - go to previous
                    updateCurrentIndex(currentIndex - 1);
                }
            }

            // Reset swipe offset with animation
            // Don't immediately reset swipeOffset to 0, let the animation handle it
            setTimeout(() => {
                setSwipeOffset(0);
                // Reset animation state after transition completes
                setTimeout(() => {
                    setIsAnimating(false);
                }, 300);
            }, 0);
        } else {
            // If it was more of a vertical swipe, immediately reset without animation
            setSwipeOffset(0);
            setIsAnimating(false);
        }
    };

    // If there's an error, show a simple error UI instead of crashing
    if (hasError) {
        return (
            <Portal containerId="modal-portal">
                <div className="modal-carousel-overlay">
                    <div
                        className="modal-carousel-clickable-background"
                        onClick={handleClose}
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            color: "white",
                            textAlign: "center",
                            padding: "20px",
                        }}
                    >
                        <h3>Something went wrong</h3>
                        <p>
                            We're sorry, but there was an error displaying this
                            content.
                        </p>
                        <button
                            onClick={handleClose}
                            style={{
                                background: "#000",
                                color: "#fff",
                                border: "1px solid #fff",
                                padding: "10px 20px",
                                marginTop: "20px",
                                cursor: "pointer",
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Portal>
        );
    }

    return (
        <Portal containerId="modal-portal">
            <div
                className={`modal-carousel-overlay ${
                    isClosing ? "closing" : ""
                } ${
                    isInteractionBlocked && isIOS
                        ? "modal-carousel-stabilizing"
                        : ""
                }`}
            >
                {isInteractionBlocked && isIOS && (
                    <div className="modal-carousel-loader-container">
                        <l-waveform
                            size="40"
                            stroke="3.5"
                            speed="1"
                            color="white"
                        ></l-waveform>
                    </div>
                )}
                <div
                    className="modal-carousel-clickable-background"
                    onClick={handleClose}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    style={{
                        touchAction:
                            isAnimating || (isInteractionBlocked && isIOS)
                                ? "none"
                                : "pan-y",
                        width: "100%",
                        maxWidth: "100vw",
                        overflow: "hidden",
                        pointerEvents:
                            isInteractionBlocked && isIOS ? "none" : "auto",
                    }}
                >
                    <div
                        className={`hover-zone left ${
                            currentIndex === 0 ? "hidden" : ""
                        }`}
                        onClick={(e) => handleNavigate("prev", e)}
                        style={getHoverZoneStyle()}
                    />
                    <div
                        className={`hover-zone right ${
                            currentIndex === content.length - 1 ? "hidden" : ""
                        }`}
                        onClick={(e) => handleNavigate("next", e)}
                        style={getHoverZoneStyle()}
                    />

                    <div
                        className={`carousel-arrow left ${
                            currentIndex === 0 ? "hidden" : ""
                        }`}
                    ></div>
                    <div
                        className={`carousel-arrow right ${
                            currentIndex === content.length - 1 ? "hidden" : ""
                        }`}
                    ></div>

                    <div
                        ref={trackRef}
                        className="modal-carousel-track"
                        onClick={(e) => e.stopPropagation()}
                        style={getTrackStyle()}
                    >
                        {content.map((item, index) => (
                            <div
                                key={index}
                                onClick={() => handleModalClick(index)}
                                className={
                                    index === currentIndex ? "in-focus" : ""
                                }
                            >
                                {"websiteLink" in item ? (
                                    <CompanyModal
                                        company={item as CompanyModalContent}
                                        onClose={handleClose}
                                        onImageClick={(
                                            imageSrc,
                                            objectFit,
                                            descriptiveTitle
                                        ) => {
                                            onImageClick(
                                                imageSrc,
                                                objectFit,
                                                descriptiveTitle
                                            );
                                        }}
                                        inFocus={index === currentIndex}
                                    />
                                ) : (
                                    <ProjectModal
                                        project={item as ProjectModalContent}
                                        onClose={handleClose}
                                        onImageClick={(
                                            imageSrc,
                                            objectFit,
                                            descriptiveTitle
                                        ) => {
                                            onImageClick(
                                                imageSrc,
                                                objectFit,
                                                descriptiveTitle
                                            );
                                        }}
                                        inFocus={index === currentIndex}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default ModalCarousel;
